import {useApi} from '@/haligator-factory';
import {CloseIcon, SearchIcon} from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import {useHalCollection, useHalState} from '@synako/haligator';
import {Navigable} from '@synako/halitrek';
import {ClientDesc, SocieteCommercialeDesc} from 'api-types/ffconnect-hal';
import {debounce} from 'lodash-es';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

const MIN_CLIENT_SEARCH_LENGTH = 3;

const colorSchemes = {
  blue: {
    selectedBg: 'blue.main_transparency_10',
    selectedColor: 'blue.main',
    hoverBg: 'gray.light',
  },
  green: {
    selectedBg: 'green.50',
    selectedColor: 'green.700',
    hoverBg: 'gray.100',
  },
};

interface ClientSearchProps {
  societeCommercialeNav: Navigable<SocieteCommercialeDesc>;
  onSelectClient: (clientId: string, clientNav: Navigable<ClientDesc>) => void;
  onClearSelection: () => void;
  selectedClientId?: string;
  colorScheme?: 'blue' | 'green';
  label?: string;
  isDisabled?: boolean;
}

/**
 * Composant de recherche de client avec autocomplétion.
 * Utilisé dans ModaleInitierNouvelleConversation et PdfNouvelImportModale.
 */
export const ClientSearch: React.FC<ClientSearchProps> = ({
  societeCommercialeNav,
  onSelectClient,
  onClearSelection,
  selectedClientId,
  colorScheme = 'blue',
  label,
  isDisabled = false,
}) => {
  const {t} = useTranslation();
  const api = useApi();
  const colors = colorSchemes[colorScheme];

  // State pour la recherche
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Récupération des clients avec la recherche
  const clientsCollection = useHalCollection(
    societeCommercialeNav.follow(
      'clients',
      debouncedSearch.length >= MIN_CLIENT_SEARCH_LENGTH
        ? {search: debouncedSearch}
        : {},
    ),
    {
      disable:
        debouncedSearch.length > 0 &&
        debouncedSearch.length < MIN_CLIENT_SEARCH_LENGTH,
    },
  );

  // Récupération du client sélectionné
  const selectedClientNav = useMemo(
    () =>
      selectedClientId
        ? api.follow('get-client', {client_id: selectedClientId})
        : undefined,
    [api, selectedClientId],
  );
  const clientHook = useHalState(selectedClientNav);

  // Debounce pour la recherche
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, 300),
    [],
  );

  // Cleanup du debounce au démontage
  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  // Gestion du changement dans l'input
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchInput(value);
      if (selectedClientId) {
        onClearSelection();
      }
      debouncedSetSearch(value);
    },
    [debouncedSetSearch, selectedClientId, onClearSelection],
  );

  // Clear la recherche
  const handleClear = useCallback(() => {
    setSearchInput('');
    setDebouncedSearch('');
    onClearSelection();
  }, [onClearSelection]);

  // Sélectionner un client
  const handleSelectClient = (clientId: string, clientName: string) => {
    const clientNav = api.follow('get-client', {client_id: clientId});
    setSearchInput(clientName);
    setDebouncedSearch('');
    onSelectClient(clientId, clientNav);
  };

  // Sync searchInput quand le client sélectionné change de l'extérieur
  useEffect(() => {
    if (selectedClientId && clientHook.data) {
      setSearchInput(clientHook.data.tolede.raison_sociale ?? selectedClientId);
    }
  }, [selectedClientId, clientHook.data]);

  const clientItems = clientsCollection.items ?? [];
  const showSearchResults =
    !selectedClientId && debouncedSearch.length >= MIN_CLIENT_SEARCH_LENGTH;

  return (
    <FormControl isRequired>
      {label && <FormLabel>{label}</FormLabel>}
      <InputGroup>
        <Input
          fontSize='sm'
          placeholder={t(
            'back_office.conversations.rechercher_client_placeholder',
          )}
          value={searchInput}
          onChange={handleSearchChange}
          isDisabled={isDisabled}
        />
        <InputRightElement>
          {searchInput ? (
            <CloseIcon
              color='gray.500'
              cursor='pointer'
              boxSize={3}
              onClick={handleClear}
            />
          ) : (
            <SearchIcon color='gray.500' />
          )}
        </InputRightElement>
      </InputGroup>

      {/* Affichage du client sélectionné */}
      {selectedClientId && clientHook.data && (
        <HStack
          mt={2}
          justifyContent='space-between'
          alignItems='center'
          p={2}
          borderRadius='md'
          bg={colors.selectedBg}
        >
          <Box
            flex='1'
            minW='0'
          >
            <Text
              color={colors.selectedColor}
              fontWeight='medium'
              fontSize='sm'
              noOfLines={1}
            >
              {clientHook.data.tolede.raison_sociale}
            </Text>
            <Text
              color={colors.selectedColor}
              fontSize='xs'
            >
              {clientHook.data.id}
            </Text>
          </Box>
          <Button
            size='xs'
            variant='unstyled'
            textColor={colors.selectedColor}
            onClick={handleClear}
            flexShrink={0}
            whiteSpace='nowrap'
          >
            {t('global.change_selection')}
          </Button>
        </HStack>
      )}

      {/* Résultats de recherche */}
      {showSearchResults && (
        <Box
          mt={2}
          border='1px solid'
          borderColor='gray.light'
          borderRadius='md'
          maxHeight='200px'
          overflowY='auto'
          bg='white'
        >
          {clientsCollection.loading && (
            <Center p={3}>
              <Spinner
                size='sm'
                mr={2}
              />
              <Text fontSize='sm'>{t('global.searching')}</Text>
            </Center>
          )}
          {!clientsCollection.loading && clientsCollection.error && (
            <Alert
              status='error'
              fontSize='sm'
            >
              <AlertIcon />
              {t('back_office.conversations.erreur_recherche_client')}
            </Alert>
          )}
          {!clientsCollection.loading &&
            !clientsCollection.error &&
            clientItems.length === 0 && (
              <Text
                p={3}
                fontSize='sm'
                color='gray.600'
              >
                {t('back_office.conversations.aucun_client_trouve')}
              </Text>
            )}
          {!clientsCollection.loading &&
            !clientsCollection.error &&
            clientItems.length > 0 && (
              <VStack
                spacing={0}
                align='stretch'
              >
                {clientItems.map((clientState) => {
                  const client = clientState.data;
                  return (
                    <Box
                      key={client.id}
                      p={3}
                      _hover={{bg: colors.hoverBg}}
                      cursor='pointer'
                      onClick={() => {
                        handleSelectClient(
                          client.id,
                          client.tolede.raison_sociale ?? client.id,
                        );
                      }}
                      borderBottom='1px solid'
                      borderColor='gray.light'
                      _last={{borderBottom: 'none'}}
                    >
                      <Text fontWeight='medium'>
                        {client.tolede.raison_sociale ?? client.id}
                      </Text>
                      <Text
                        fontSize='xs'
                        color='gray.dark'
                      >
                        {client.id}
                      </Text>
                    </Box>
                  );
                })}
              </VStack>
            )}
        </Box>
      )}
    </FormControl>
  );
};
